import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'
import { ProfileData, OccupationGroup, ParentCategory } from '@/types/profile'

export interface User {
  id: number
  email: string
  name?: string
  avatar?: string
  isAuthenticated: boolean
}

// プロフィールデータの型定義（/api/members/profile レスポンス）
export type MemberProfile = ProfileData

// メール通知設定の型定義（/api/members/{memberId}/settings/email_notification レスポンス）
export interface EmailNotificationSettings {
  publishedNotice: boolean
  emailmagazineForStudent: boolean
  emailmagazineForJobseeker: boolean
  receiveTalentbookNotice: boolean
}

interface UserState {
  welcomeMessageShown: Record<number, boolean>
  currentUser: User | null
  profile: MemberProfile | null // プロフィールデータ
  emailNotification: EmailNotificationSettings | null // メール通知設定
  occupationGroups: OccupationGroup[] // 職種グループ
  parentCategories: ParentCategory[] // 業種カテゴリ
  // 学歴の学校・学部・学科options
  universityOptions: Array<{ id: number; name: string }>
  currentUniversityId: number | null
  facultyOptions: Array<{ id: number; name: string }>
  currentFacultyId: number | null
  departmentOptions: Array<{ id: number; name: string }>
  isLoading: boolean
  error: string | null
  loginError: boolean
  authCheckCompleted: boolean // Track if auth check has been completed
}

const initialState: UserState = {
  welcomeMessageShown: {},
  currentUser: null,
  profile: null,
  emailNotification: null,
  occupationGroups: [],
  parentCategories: [],
  universityOptions: [],
  currentUniversityId: null,
  facultyOptions: [],
  currentFacultyId: null,
  departmentOptions: [],
  isLoading: false,
  error: null,
  loginError: false,
  authCheckCompleted: false,
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    markWelcomeMessageShown: (state, action: PayloadAction<number>) => {
      const memberId = action.payload
      if (memberId) {
        state.welcomeMessageShown[memberId] = true
      }
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload
      state.error = null
      state.loginError = false
      state.authCheckCompleted = true
    },
    clearUser: (state) => {
      state.currentUser = null
      state.error = null
      state.loginError = false
      state.authCheckCompleted = true
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
    setLoginError: (state, action: PayloadAction<boolean>) => {
      state.loginError = action.payload
    },
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload
      state.isLoading = false
      state.error = null
      state.loginError = false
      state.authCheckCompleted = true
    },
    loginFailure: (state) => {
      state.currentUser = null
      state.isLoading = false
      state.loginError = true
      state.authCheckCompleted = true
    },
    logout: (state) => {
      state.currentUser = null
      state.profile = null
      state.emailNotification = null
      state.occupationGroups = []
      state.parentCategories = []
      state.isLoading = false
      state.error = null
      state.loginError = false
      state.authCheckCompleted = false // Reset on logout so next login can check again
    },
    setProfile: (state, action: PayloadAction<MemberProfile>) => {
      state.profile = action.payload
    },
    updateProfile: (state, action: PayloadAction<Partial<MemberProfile>>) => {
      state.profile = state.profile
        ? { ...state.profile, ...action.payload }
        : (action.payload as MemberProfile)
    },
    setEmailNotification: (state, action: PayloadAction<EmailNotificationSettings>) => {
      state.emailNotification = action.payload
    },
    updateEmailNotification: (state, action: PayloadAction<Partial<EmailNotificationSettings>>) => {
      if (state.emailNotification) {
        state.emailNotification = { ...state.emailNotification, ...action.payload }
      }
    },
    setOccupationGroups: (state, action: PayloadAction<OccupationGroup[]>) => {
      state.occupationGroups = action.payload
    },
    setParentCategories: (state, action: PayloadAction<ParentCategory[]>) => {
      state.parentCategories = action.payload
    },
    setUniversityOptions: (state, action: PayloadAction<Array<{ id: number; name: string }>>) => {
      state.universityOptions = action.payload
    },
    setFacultyOptions: (
      state,
      action: PayloadAction<{
        universityId: number
        faculties: Array<{ id: number; name: string }>
      }>,
    ) => {
      state.currentUniversityId = action.payload.universityId
      state.facultyOptions = action.payload.faculties
    },
    setDepartmentOptions: (
      state,
      action: PayloadAction<{
        facultyId: number
        departments: Array<{ id: number; name: string }>
      }>,
    ) => {
      state.currentFacultyId = action.payload.facultyId
      state.departmentOptions = action.payload.departments
    },
    clearEducationOptions: (state) => {
      state.universityOptions = []
      state.currentUniversityId = null
      state.facultyOptions = []
      state.currentFacultyId = null
      state.departmentOptions = []
    },
  },
})

export const {
  markWelcomeMessageShown,
  setUser,
  clearUser,
  setLoading,
  setError,
  clearError,
  setLoginError,
  loginSuccess,
  loginFailure,
  logout,
  setProfile,
  updateProfile,
  setEmailNotification,
  updateEmailNotification,
  setOccupationGroups,
  setParentCategories,
  setUniversityOptions,
  setFacultyOptions,
  setDepartmentOptions,
  clearEducationOptions,
} = userSlice.actions

export const selectWelcomeMessageShown = (state: RootState, memberId: number): boolean =>
  state.user.welcomeMessageShown[memberId] || false

export const selectCurrentUser = (state: RootState): User | null => state.user.currentUser
export const selectIsAuthenticated = (state: RootState): boolean =>
  state.user.currentUser?.isAuthenticated || false
export const selectUserLoading = (state: RootState): boolean => state.user.isLoading
export const selectUserError = (state: RootState): string | null => state.user.error
export const selectLoginError = (state: RootState): boolean => state.user.loginError
export const selectProfile = (state: RootState): MemberProfile | null => state.user.profile
export const selectEmailNotification = (state: RootState): EmailNotificationSettings | null =>
  state.user.emailNotification
export const selectOccupationGroups = (state: RootState): OccupationGroup[] =>
  state.user.occupationGroups
export const selectParentCategories = (state: RootState): ParentCategory[] =>
  state.user.parentCategories
export const selectUniversityOptions = (state: RootState): Array<{ id: number; name: string }> =>
  state.user.universityOptions
export const selectCurrentUniversityId = (state: RootState): number | null =>
  state.user.currentUniversityId
export const selectFacultyOptions = (state: RootState): Array<{ id: number; name: string }> =>
  state.user.facultyOptions
export const selectCurrentFacultyId = (state: RootState): number | null =>
  state.user.currentFacultyId
export const selectDepartmentOptions = (state: RootState): Array<{ id: number; name: string }> =>
  state.user.departmentOptions
export const selectAuthCheckCompleted = (state: RootState): boolean => state.user.authCheckCompleted

export default userSlice.reducer
